import { useState, useEffect } from 'react';
import { Send, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

const LABELS = {
  it: { connect: 'Collega Telegram', connected: 'Telegram collegato', disconnect: 'Scollega',
        hint: 'Si apre il bot: premi "Avvia" e il collegamento è fatto.',
        pro: 'Le notifiche Telegram sono una funzione Pro.',
        modeTop: 'Solo affari top', modeAll: 'Tutti gli annunci',
        modeLabel: 'Cosa ricevi su Telegram',
        modeHintUnlinked: 'Scegli adesso: diventa attivo quando colleghi Telegram.',
        modeHint: 'Solo affari top: 3 annunci migliori con foto + riepilogo. Tutti: elenco completo.' },
  de: { connect: 'Telegram verbinden', connected: 'Telegram verbunden', disconnect: 'Trennen',
        hint: 'Der Bot öffnet sich: auf "Start" tippen, fertig.',
        pro: 'Telegram-Benachrichtigungen sind eine Pro-Funktion.',
        modeTop: 'Nur Top-Deals', modeAll: 'Alle Inserate',
        modeLabel: 'Was du auf Telegram bekommst',
        modeHintUnlinked: 'Jetzt wählen: aktiv, sobald Telegram verbunden ist.',
        modeHint: 'Top-Deals: 3 beste Inserate mit Foto + Zusammenfassung. Alle: ganze Liste.' },
  fr: { connect: 'Connecter Telegram', connected: 'Telegram connecté', disconnect: 'Déconnecter',
        hint: 'Le bot s\'ouvre : appuie sur "Démarrer", c\'est fait.',
        pro: 'Les notifications Telegram sont une fonction Pro.',
        modeTop: 'Meilleures affaires', modeAll: 'Toutes les annonces',
        modeLabel: 'Ce que tu reçois sur Telegram',
        modeHintUnlinked: 'Choisis maintenant : actif dès que Telegram est connecté.',
        modeHint: 'Meilleures affaires : 3 annonces avec photo + résumé. Toutes : liste complète.' },
  en: { connect: 'Connect Telegram', connected: 'Telegram connected', disconnect: 'Disconnect',
        hint: 'The bot opens: tap "Start" and you are connected.',
        pro: 'Telegram notifications are a Pro feature.',
        modeTop: 'Top deals only', modeAll: 'All listings',
        modeLabel: 'What you get on Telegram',
        modeHintUnlinked: 'Pick it now: it becomes active as soon as Telegram is linked.',
        modeHint: 'Top deals: 3 best listings with photo + summary. All: full list.' },
};

/** Selettore "solo affari top" / "tutti gli annunci": riusato nel form e sulle card avviso. */
export const TelegramModeToggle = ({ lang = 'it', mode = 'top', onChange, linked = true, showLabel = false, idSuffix = '' }) => {
  const L = LABELS[lang] || LABELS.it;
  return (
    <div data-testid={`telegram-mode-toggle${idSuffix ? `-${idSuffix}` : ''}`}>
      {showLabel && <p className="text-xs font-medium text-muted-foreground mb-1">{L.modeLabel}</p>}
      <div className="inline-flex p-0.5 rounded-lg bg-muted/70 border border-border/60">
        {[['top', L.modeTop], ['all', L.modeAll]].map(([value, label]) => (
          <button key={value} type="button" onClick={() => onChange(value)}
            data-testid={`telegram-mode-${value}${idSuffix ? `-${idSuffix}` : ''}`}
            className={`text-xs px-2.5 h-7 rounded-md transition-colors ${
              mode === value ? 'bg-background shadow-sm font-semibold' : 'text-muted-foreground'}`}>
            {label}
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-1">{linked ? L.modeHint : L.modeHintUnlinked}</p>
    </div>
  );
};

export const TelegramLinkButton = ({ lang = 'it', mode, onModeChange, onLinkedChange }) => {
  const L = LABELS[lang] || LABELS.it;
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const applyStatus = (s) => {
    setStatus(s);
    onLinkedChange?.(!!s?.linked);
  };

  useEffect(() => {
    api.getTelegramStatus().then(applyStatus).catch(() => applyStatus({ linked: false }));
  }, []);

  const handleConnect = async () => {
    setLoading(true);
    try {
      const res = await api.createTelegramLinkToken();
      window.open(res.deep_link, '_blank', 'noopener');
      // Il collegamento avviene su Telegram: rileggiamo lo stato dopo pochi secondi.
      setTimeout(() => api.getTelegramStatus().then(applyStatus).catch(() => {}), 6000);
    } catch (e) {
      toast.error(e?.detail?.code === 'PRO_REQUIRED' ? L.pro : 'Errore');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    await api.unlinkTelegram();
    applyStatus({ linked: false });
  };

  return (
    <div data-testid="telegram-link-block">
      <div className="flex items-center gap-2">
        {status?.linked ? (
          <>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400"
              data-testid="telegram-linked-badge">
              <Check size={13} /> {L.connected} {status.chat_id_masked}
            </span>
            <button type="button" onClick={handleDisconnect} data-testid="telegram-unlink-button"
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2">
              {L.disconnect}
            </button>
          </>
        ) : (
          <button type="button" onClick={handleConnect} disabled={loading}
            data-testid="telegram-connect-button"
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 h-8 rounded-lg border border-sky-500/40 text-sky-600 dark:text-sky-400 hover:bg-sky-500/10 active:scale-[0.97] transition-all disabled:opacity-60">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            {L.connect}
          </button>
        )}
      </div>
      {!status?.linked && <p className="text-xs text-muted-foreground mt-1">{L.hint}</p>}

      {onModeChange && (
        <div className="mt-2">
          <TelegramModeToggle lang={lang} mode={mode} onChange={onModeChange}
            linked={!!status?.linked} showLabel />
        </div>
      )}
    </div>
  );
};
